export type FeatureFlag = {
  key: string;
  label: string;
  description?: string;
  defaultValue?: boolean;
};

export const AUTO_ENABLED_FLAGS: ReadonlySet<string> = new Set(
  (import.meta.env.VITE_AUTO_ENABLED_FEATURE_FLAGS ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
);

export const ONBOARDING_RECOMMENDATIONS_FLAG = "onboarding-recommendations";
export const PORTFOLIO_MEMBERS_FLAG = "portfolio-members";
export const BUNDLE_MANIFEST_CHECKOUT_FLAG = "bundle-manifest-checkout";

export const FEATURE_FLAGS: FeatureFlag[] = [
  {
    key: ONBOARDING_RECOMMENDATIONS_FLAG,
    label: "Onboarding + For You dashboard",
    description:
      "Funnel users through the interest-capture onboarding wizard and show the personalized For You dashboard tab.",
    // On by default for the staging rollout (funnels all users through
    // onboarding + shows the For You tab). Set back to false before prod
    // unless the feature is meant to be live in production.
    defaultValue: true,
  },
  {
    key: PORTFOLIO_MEMBERS_FLAG,
    label: "Portfolio members beyond projects",
    description:
      "Let portfolios hold researcher fundraisers and funding pools as members (requires the backend PORTFOLIO_MEMBERS_V2 flag).",
    // On by default for the staging rollout (backend flag is on there). Set
    // back to false before prod unless production's PORTFOLIO_MEMBERS_V2 is
    // also being enabled — the UI is inert without it, but hides better.
    defaultValue: true,
  },
  {
    key: BUNDLE_MANIFEST_CHECKOUT_FLAG,
    label: "SciOS manifest attach (mirrored portfolios)",
    description:
      "On a mirrored SciOS bundle portfolio, let a funder attach the Fund page's manifest export and pay through the bundle checkout (requires the backend BUNDLE_MANIFEST_CHECKOUT flag).",
    // On by default for the staging rollout (backend BUNDLE_MANIFEST_CHECKOUT
    // is on there, same convention as portfolio-members above). Keep false on
    // main until production's backend flag is enabled — the UI 404s without it.
    defaultValue: false,
  },
];

/**
 * Exactly who sees the feature-flag menu.
 *
 * This was a suffix match on @rndcatalyst.com, which was too wide in a way
 * that mattered: account creation through the NSF-pilot funnel is public and
 * unverified, so anyone could claim an address on the domain and read our
 * unreleased feature names. A staff-shaped address is not proof of staff.
 *
 * An exact allow-list is the whole rule. Sub-addressing does not widen it
 * either — amit+test@ is a different string from amit@, so a throwaway test
 * account does not inherit access.
 */
const PRIVILEGED_EMAILS: ReadonlySet<string> = new Set([
  "amit@rndcatalyst.com",
  "marion@rndcatalyst.com",
]);

export const isPrivilegedUser = (email?: string | null): boolean => {
  if (!email) return false;
  return PRIVILEGED_EMAILS.has(email.trim().toLowerCase());
};
